import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { sendersService } from '../services/senders.service';
import {
  UpdateConfigurationDto,
  UpdateSenderDto,
  ValidateSenderDto,
} from '../types/sender.types';
import { useOrganisationsStore } from '@/store/organisations.store';

// Query keys
export const senderKeys = {
  all: ['senders'] as const,
  list: (orgUuid?: string) => ['senders', 'list', orgUuid] as const,
  detail: (uuid: string) => ['senders', uuid] as const,
  configurations: ['sender-configurations'] as const,
  default: ['senders', 'default'] as const,
  brevo: ['senders', 'brevo'] as const,
};

// Main senders queries
export const useSenders = () => {
  const orgUuid = useOrganisationsStore(s => s.currentOrganisation?.uuid);
  return useQuery({
    queryKey: senderKeys.list(orgUuid),
    queryFn: () => sendersService.getAll(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useSender = (uuid: string) => {
  return useQuery({
    queryKey: senderKeys.detail(uuid),
    queryFn: () => sendersService.getById(uuid),
    enabled: !!uuid,
  });
};

export const useCurrentDefaultSender = () => {
  return useQuery({
    queryKey: senderKeys.default,
    queryFn: sendersService.getCurrentDefault,
    staleTime: 5 * 60 * 1000,
  });
};

// Configurations queries
export const useSenderConfigurations = () => {
  return useQuery({
    queryKey: senderKeys.configurations,
    queryFn: sendersService.getConfigurations,
    staleTime: 5 * 60 * 1000,
  });
};

// Brevo integration
export const useBrevoSenders = () => {
  return useQuery({
    queryKey: senderKeys.brevo,
    queryFn: sendersService.getBrevoSenders,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Senders mutations
export const useCreateSender = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sender: Parameters<typeof sendersService.create>[0]) =>
      sendersService.create(sender),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: senderKeys.all });
      toast.success('Sender created successfully');
    },
    onError: (error: HttpApiError) => {
      console.log({
        error: error.response?.data?.message || 'Failed to create sender',
      });
      toast.error(error.response?.data?.message || 'Failed to create sender');
    },
  });
};

export const useUpdateSender = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ uuid, data }: { uuid: string; data: UpdateSenderDto }) =>
      sendersService.update(uuid, data),
    onSuccess: (_, { uuid }) => {
      queryClient.invalidateQueries({ queryKey: senderKeys.all });
      queryClient.invalidateQueries({ queryKey: senderKeys.detail(uuid) });
      toast.success('Sender updated successfully');
    },
    onError: (error: HttpApiError) => {
      toast.error(error.response?.data?.message || 'Failed to update sender');
    },
  });
};

export const useDeleteSender = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: sendersService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: senderKeys.all });
      queryClient.invalidateQueries({ queryKey: senderKeys.default });
      toast.success('Sender deleted successfully');
    },
    onError: (error: HttpApiError) => {
      toast.error(error.response?.data?.message || 'Failed to delete sender');
    },
  });
};

export const useValidateSender = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ uuid, otp }: { uuid: string; otp: ValidateSenderDto }) =>
      sendersService.validate(uuid, otp),
    onSuccess: (_, { uuid }) => {
      queryClient.invalidateQueries({ queryKey: senderKeys.all });
      queryClient.invalidateQueries({ queryKey: senderKeys.detail(uuid) });
      toast.success('Sender validated successfully');
    },
    onError: (error: HttpApiError) => {
      toast.error(error.response?.data?.message || 'Failed to validate sender');
    },
  });
};

export const useSetDefaultSender = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: sendersService.setDefault,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: senderKeys.all });
      queryClient.invalidateQueries({ queryKey: senderKeys.default });
      toast.success('Default sender updated successfully');
    },
    onError: (error: HttpApiError) => {
      toast.error(
        error.response?.data?.message || 'Failed to set default sender'
      );
    },
  });
};

// Configuration mutations
export const useCreateConfiguration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: sendersService.createConfiguration,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: senderKeys.configurations });
      toast.success('Configuration created successfully');
    },
    onError: (error: HttpApiError) => {
      toast.error(
        error.response?.data?.message || 'Failed to create configuration'
      );
    },
  });
};

export const useUpdateConfiguration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      uuid,
      data,
    }: {
      uuid: string;
      data: UpdateConfigurationDto;
    }) => sendersService.updateConfiguration(uuid, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: senderKeys.configurations });
      toast.success('Configuration updated successfully');
    },
    onError: (error: HttpApiError) => {
      toast.error(
        error.response?.data?.message || 'Failed to update configuration'
      );
    },
  });
};

export const useDeleteConfiguration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: sendersService.deleteConfiguration,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: senderKeys.configurations });
      toast.success('Configuration deleted successfully');
    },
    onError: (error: HttpApiError) => {
      toast.error(
        error.response?.data?.message || 'Failed to delete configuration'
      );
    },
  });
};

// OAuth
export const useGetOAuthUrl = () => {
  return useMutation({
    mutationFn: () => sendersService.getOAuthAuthorizeUrl(),
    onError: (error: HttpApiError) => {
      toast.error(
        error.response?.data?.message ||
          "Impossible d'obtenir l'URL d'autorisation"
      );
    },
  });
};

export const useDisconnectOAuth = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (uuid: string) => sendersService.disconnectOAuth(uuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: senderKeys.all });
      toast.success('Microsoft 365 déconnecté avec succès');
    },
    onError: (error: HttpApiError) => {
      toast.error(
        error.response?.data?.message || 'Échec de la déconnexion OAuth'
      );
    },
  });
};

// Brevo sync
export const useSyncWithBrevo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: sendersService.syncWithBrevo,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: senderKeys.all });
      queryClient.invalidateQueries({ queryKey: senderKeys.brevo });
      toast.success('Senders synced with Brevo successfully');
    },
    onError: (error: HttpApiError) => {
      toast.error(error.response?.data?.message || 'Failed to sync with Brevo');
    },
  });
};
